import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Pagination,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PoolProblem } from '../../../../services/problemPoolService';
import RichTextDisplay from '../../../../components/common/RichTextDisplay';
import { stripHtmlTags } from '../../../../utils/textUtils';

interface ProblemPoolListProps {
  problems: PoolProblem[];
  total: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onEdit: (problem: PoolProblem) => void;
  onDelete: (problemId: number) => void;
}

const difficultyStars = (level: number): string => {
  return '★'.repeat(level) + '☆'.repeat(5 - level);
};

const ProblemPoolList: React.FC<ProblemPoolListProps> = ({
  problems,
  total,
  page,
  totalPages,
  onPageChange,
  onEdit,
  onDelete
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [problemToDelete, setProblemToDelete] = useState<PoolProblem | null>(null);

  const handleDeleteClick = (problem: PoolProblem) => {
    setProblemToDelete(problem);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (problemToDelete) {
      onDelete(problemToDelete.id);
    }
    setDeleteDialogOpen(false);
    setProblemToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setProblemToDelete(null);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    onPageChange(value);
  };

  if (problems.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No problems found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters or create a new problem
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {problems.length} of {total} problems
      </Typography>

      {/* Problem cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {problems.map(problem => (
          <Paper key={problem.id} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Main content */}
              <Box sx={{ flex: 1, pr: 2 }}>
                {/* Title and metadata row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {problem.title && (
                    <Typography
                      variant="subtitle1"
                      fontWeight="medium"
                      onClick={() => onEdit(problem)}
                      sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                    >
                      {stripHtmlTags(problem.title)}
                    </Typography>
                  )}
                  {problem.categoryName && (
                    <Chip
                      label={problem.categoryName}
                      size="small"
                      sx={{ bgcolor: problem.categoryColor || '#6366F1', color: 'white' }}
                    />
                  )}
                  <Chip
                    label={problem.problemType === 'multiple_choice' ? 'MC' : 'Open'}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {difficultyStars(problem.difficultyLevel)}
                  </Typography>
                </Box>

                {/* Problem statement preview */}
                <Box
                  onClick={() => onEdit(problem)}
                  sx={{ cursor: 'pointer', mb: 1 }}
                >
                  <RichTextDisplay
                    content={problem.problemStatement}
                    variant="body2"
                    color="text.secondary"
                    preview
                    maxLength={200}
                  />
                </Box>

                {/* Tags */}
                {problem.tags && problem.tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {problem.tags.map(tag => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        sx={{
                          bgcolor: tag.color,
                          color: 'white',
                          height: 20,
                          '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Footer info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {problem.source && (
                    <Typography variant="caption" color="text.secondary">
                      Source: {problem.source}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Used {problem.usageCount} times
                  </Typography>
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEdit(problem)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Usage count">
                  <IconButton size="small" disabled>
                    <ContentCopyIcon fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      {problem.usageCount}
                    </Typography>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(problem)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Problem</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this problem? This action cannot be undone.
            {problemToDelete?.usageCount ? (
              <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
                Warning: This problem has been used {problemToDelete.usageCount} times in sessions.
                Deleting it will not affect the cloned copies in those sessions.
              </Box>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProblemPoolList;
